/**
 * O formulário do perfil do favorecido: o que os campos guardam, como abrem e
 * o que "Salvar Perfil" envia.
 *
 * Papel: aqui ficam `ValoresDoPerfil` — os campos como a tela os mostra, em
 * texto mascarado —, `valoresDoFavorecido`, que os preenche ao abrir
 * (`openEditFavorecido`, l. 1951–2033), `totaisDoFormulario`, que escreve o
 * total de cada linha e o "Total a receber (30 dias)" (l. 2054–2075), e
 * `corpoDoPerfil`, que monta o que `perfilDoFavorecidoSchema` valida e a API
 * grava (`saveFavModal`, l. 2096–2163).
 *
 * Como usar: os valores com até quatro casas (as diárias e a diarista) usam
 * `mascararDecimalLivre`; os únicos (auxílio e INSS) usam `mascararCentavos`; e
 * todos voltam a número por `lerValorDigitado`, que lê o campo vazio como zero
 * — é a linha marcada sem valor gravando zero, como na origem. A linha
 * desmarcada vai nula. As limpezas — conta apartada desmarcada, categoria que
 * não é "Despesas com Pessoal" ou "Petrella" — não são feitas aqui: o
 * `perfilDoFavorecidoSchema` as aplica, na tela e na API, com a mesma regra.
 */
import {
  type Favorecido,
  type FormaPagamentoLancamento,
  type TipoItemAjudaCusto,
  totaisDoPerfil,
} from '@concierge/shared';

import { lerValorDigitado, mascararCpf } from '@/shared/lib/mascara';
import { formatarAteQuatroCasas, formatarReais } from './formato';

/** Uma linha de ajuda de custo: a caixa e o valor como está no campo. */
export interface LinhaDaAjuda {
  marcada: boolean;
  valor: string;
}

/** Os campos do perfil, como a tela os mostra. */
export interface ValoresDoPerfil {
  /** O CPF mascarado. */
  cpf: string;
  contaApartada: boolean;
  debitoDe: string;
  banco: string;
  agencia: string;
  conta: string;
  tipoConta: 'Corrente' | 'Poupança';
  /** Vazio é "— Nenhuma —". */
  categoriaPadrao: string;
  subcategoriaPadrao: string;
  formaPagamentoPadrao: FormaPagamentoLancamento | '';
  ajuda: Readonly<Record<TipoItemAjudaCusto, LinhaDaAjuda>>;
  /** O auxílio alimentação: pago à parte, fora de `ajuda` e do total de 30 dias. */
  alimentacao: LinhaDaAjuda;
  recorrente: boolean;
  /** O mês da recorrência, de 1 a 12. */
  recorrenciaMes: number;
  /** O ano da recorrência, como está no campo. */
  recorrenciaAno: string;
  diaristaHabilitada: boolean;
  diaristaValor: string;
}

/**
 * Uma linha de ajuda de custo a partir do valor gravado.
 *
 * @param valor - O valor gravado; nulo é a linha desmarcada.
 * @returns A caixa marcada com o valor em até quatro casas, ou desmarcada e vazia.
 */
function linhaGravada(valor: number | null): LinhaDaAjuda {
  return valor === null
    ? { marcada: false, valor: '' }
    : { marcada: true, valor: formatarAteQuatroCasas(valor) };
}

/**
 * Os campos do perfil ao abrir.
 *
 * @param favorecido - O favorecido como a API o devolve.
 * @param hoje - A data de hoje, em hora local, para o mês e o ano da
 *   recorrência ainda não ligada.
 * @returns Os campos preenchidos com o que está gravado.
 */
export function valoresDoFavorecido(favorecido: Favorecido, hoje: string): ValoresDoPerfil {
  const { ajudaCusto } = favorecido;
  const desde = ajudaCusto.recorrenciaDesde;
  return {
    cpf: mascararCpf(favorecido.cpf ?? ''),
    contaApartada: favorecido.contaApartada,
    debitoDe: favorecido.contaApartadaDebitoDe ?? '',
    banco: favorecido.contaApartadaBanco ?? '',
    agencia: favorecido.contaApartadaAgencia ?? '',
    conta: favorecido.contaApartadaConta ?? '',
    tipoConta: favorecido.contaApartadaTipo === 'Poupança' ? 'Poupança' : 'Corrente',
    categoriaPadrao: favorecido.categoriaPadrao ?? '',
    subcategoriaPadrao: favorecido.subcategoriaPadrao ?? '',
    formaPagamentoPadrao: favorecido.formaPagamentoPadrao ?? '',
    ajuda: {
      custo: linhaGravada(ajudaCusto.custoDiaria),
      diarias: linhaGravada(ajudaCusto.diariasDiaria),
      medica: linhaGravada(ajudaCusto.medicaValor),
      inss: linhaGravada(ajudaCusto.inssValor),
    },
    alimentacao: linhaGravada(ajudaCusto.alimentacaoValor),
    recorrente: desde !== null,
    recorrenciaMes: Number((desde ?? hoje).slice(5, 7)),
    recorrenciaAno: (desde ?? hoje).slice(0, 4),
    diaristaHabilitada: favorecido.diaristaHabilitada,
    diaristaValor:
      favorecido.diaristaValorDiaria === null
        ? ''
        : formatarAteQuatroCasas(favorecido.diaristaValorDiaria),
  };
}

/**
 * O valor de uma linha marcada, ou nulo na desmarcada.
 *
 * @param linha - A linha.
 * @returns O número lido do campo, zero quando vazio.
 */
function valorDaLinha(linha: LinhaDaAjuda): number | null {
  return linha.marcada ? lerValorDigitado(linha.valor) : null;
}

/** Os três totais escritos do quadro de ajudas de custo. */
export interface TotaisDoFormulario {
  /** Ajuda de Custo × 30, ou "—" desmarcada. */
  custo: string;
  /** Diárias × 30, ou "—" desmarcada. */
  diarias: string;
  /** A soma das linhas marcadas. */
  total: string;
}

/**
 * Os totais do quadro de ajudas de custo, como a tela os escreve.
 *
 * @param ajuda - As quatro linhas.
 * @returns O total de Ajuda de Custo, o de Diárias e o geral.
 */
export function totaisDoFormulario(ajuda: ValoresDoPerfil['ajuda']): TotaisDoFormulario {
  const totais = totaisDoPerfil({
    custoDiaria: valorDaLinha(ajuda.custo),
    diariasDiaria: valorDaLinha(ajuda.diarias),
    medicaValor: valorDaLinha(ajuda.medica),
    inssValor: valorDaLinha(ajuda.inss),
  });
  return {
    custo: totais.custo === null ? '—' : formatarReais(totais.custo),
    diarias: totais.diarias === null ? '—' : formatarReais(totais.diarias),
    total: formatarReais(totais.total),
  };
}

/**
 * O que "Salvar Perfil" envia, antes do `perfilDoFavorecidoSchema`.
 *
 * @param valores - Os campos.
 * @returns O corpo, com as linhas desmarcadas nulas, a marcada vazia em zero e
 *   a recorrência em `AAAA-MM`.
 */
export function corpoDoPerfil(valores: ValoresDoPerfil) {
  return {
    categoriaPadrao: valores.categoriaPadrao,
    subcategoriaPadrao: valores.subcategoriaPadrao,
    formaPagamentoPadrao:
      valores.formaPagamentoPadrao === '' ? null : valores.formaPagamentoPadrao,
    cpf: valores.cpf,
    contaApartada: valores.contaApartada,
    contaApartadaDebitoDe: valores.debitoDe,
    contaApartadaBanco: valores.banco,
    contaApartadaAgencia: valores.agencia,
    contaApartadaConta: valores.conta,
    contaApartadaTipo: valores.tipoConta,
    ajudaCusto: {
      custoDiaria: valorDaLinha(valores.ajuda.custo),
      diariasDiaria: valorDaLinha(valores.ajuda.diarias),
      medicaValor: valorDaLinha(valores.ajuda.medica),
      inssValor: valorDaLinha(valores.ajuda.inss),
      alimentacaoValor: valorDaLinha(valores.alimentacao),
      recorrenciaDesde: valores.recorrente
        ? `${valores.recorrenciaAno.trim()}-${String(valores.recorrenciaMes).padStart(2, '0')}`
        : null,
    },
    diaristaHabilitada: valores.diaristaHabilitada,
    diaristaValorDiaria: valores.diaristaHabilitada
      ? lerValorDigitado(valores.diaristaValor)
      : null,
  };
}
